import React, { useState, useEffect } from 'react';
import { GTM_CLICK_AGENT_PAGE_LOCATION_SEARCH_BAR, GTM_CLICK_AGENT_PAGE_EDIT_COUNTRY } from 'constants/events';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { SuggestionDropdown } from 'components/agent-search';
import * as AgentActions from 'utils/agent-endpoint';
import { CountryCodeList } from 'types/countries';
import { trackEvent } from 'utils/google-tag-manager';
import { agentSearchIds } from 'constants/test-constants';
import { useThemeContext } from 'contexts';
import { ThemeNames as TenantNames } from 'types/themes';
import { buildClassName } from 'utils/build-class-name';
import NextImage from 'next/image';
import styles from './style.module.scss';
import CloseIcon from 'components/icon/close-icon';
import { useTranslation } from 'next-i18next';
import { AGENT_SEARCH_NAMESPACE, LOCALE_AGENT_SEARCH_PATHS } from 'types/locales';

interface InputWithCountrySelectProps {
  onCountryChange: (countryCode: CountryCodeList) => void;
  country: CountryCodeList;
  onLocationSelect: (location: string) => void;
  locationQuery: string;
  updateLocationQuery: (query: string) => void;
  allowCountryChange?: boolean;
}

export default function InputWithCountrySelect({ onCountryChange, country, onLocationSelect, locationQuery, updateLocationQuery, allowCountryChange = true }: InputWithCountrySelectProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const outsideClickHandler = useOutsideClickHandler<HTMLDivElement>(() => setShowDropdown(false));
  const { themeName: tenantName } = useThemeContext();
  const { t } = useTranslation(AGENT_SEARCH_NAMESPACE);

  const isCommercial = tenantName === TenantNames.EXP_COMMERCIAL;

  useEffect(() => {
    const fetchLocations = async () => {
      const data = await AgentActions.locationSuggestions(locationQuery, country);
      if (!(data instanceof Error)) {
        if (data) {
          setLocations(data);
        } else {
          setLocations([]);
        }
      }
    };

    if (locationQuery.length > 0) fetchLocations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationQuery]);

  const countries = [
    { code: CountryCodeList.UNITED_STATES, name: t(LOCALE_AGENT_SEARCH_PATHS.COUNTRIES_UNITED_STATES), flagUrl: '/next/assets/images/us-flag.svg' },
    { code: CountryCodeList.CANADA, name: t(LOCALE_AGENT_SEARCH_PATHS.COUNTRIES_CANADA), flagUrl: '/next/assets/images/ca-flag.svg' },
  ];

  const handleCountrySelect = (countryCode: CountryCodeList) => {
    onCountryChange(countryCode);
    setShowDropdown(false);
  };

  const handlesLocationQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateLocationQuery(e.target.value);
    setShowSuggestions(!!e.target.value);
  };

  const handleSelectLocation = (l: string) => {
    onLocationSelect(l);
    updateLocationQuery(l);
    setLocations([]);
    setShowSuggestions(false);
  };

  const handleClickEvent = () => {
    if (!isCommercial) trackEvent(GTM_CLICK_AGENT_PAGE_EDIT_COUNTRY);
    setShowDropdown(!showDropdown);
  };

  const handleClearLocationQuery = () => {
    updateLocationQuery('');
    setShowSuggestions(false);
    onLocationSelect('');
  };

  return (
    <div className={buildClassName(styles['input-wrapper'], isCommercial && styles['is-commercial'], locationQuery && styles['has-query'])}>
      <div className={styles.dropdown} ref={outsideClickHandler}>
        {allowCountryChange ? (
          <div onClick={handleClickEvent} className={styles['drop-button']} data-testid={agentSearchIds.countryDropdown}>
            {country === CountryCodeList.UNITED_STATES
              ? <NextImage width={30} height={30} src={countries[0].flagUrl} alt="Canada Flag" />
              : <NextImage width={30} height={30} src={countries[1].flagUrl} alt="Canada Flag" />
            }
            <ArrowDropDown style={{ padding: 0, margin: 0 }} />
          </div>
        ) : (
          <div className={styles['drop-button']} style={{ cursor: 'default' }} data-testid={agentSearchIds.countryDropdown}>
            {country === CountryCodeList.UNITED_STATES
              ? <NextImage width={35} height={35} src={countries[0].flagUrl} alt="Canada Flag" />
              : <NextImage width={35} height={35} src={countries[1].flagUrl} alt="Canada Flag" />
            }
          </div>
        )}
        {showDropdown && (
          <div className={styles['dropdown-content']}>
            {countries.map((country: any) => (
              <div className={styles['drop-option']} key={country.code} onClick={() => handleCountrySelect(country.code)} data-testid={agentSearchIds.countryDropdownOption}>
                <NextImage width={25} height={25} src={country.flagUrl} alt={`${country.name} Flag`} />
              </div>
            ))}
          </div>
        )}
        {/* location suggestion dropdown */}
        {locations.length > 0 && showSuggestions &&
          <SuggestionDropdown suggestions={locations} type={'location'} onSelect={handleSelectLocation} onClose={() => setShowSuggestions(false)} />}
      </div>
      <input
        type="text"
        placeholder={t(LOCALE_AGENT_SEARCH_PATHS.PLACEHOLDERS_LOCATION, { defaultValue: 'Location' })}
        data-testid={agentSearchIds.locationInput}
        onInput={handlesLocationQueryChange}
        value={locationQuery}
        onClick={() => trackEvent(GTM_CLICK_AGENT_PAGE_LOCATION_SEARCH_BAR)}
      />
      {locationQuery && (
        <CloseIcon className={styles['clear-button']} onClick={handleClearLocationQuery} />
      )}
    </div>
  );
}
